// src/components/shell/popovers/ActivityBannerPanel.tsx
// Panel body for ActivityBannerPopover. Extracted in the round-2 closeout
// slice to keep ActivityBannerPopover under the file-size-hygiene soft cap
// once the ad-alerts rollup landed. Renders the impersonation strip (when
// active), the notifications section, the ad-alerts section, and the
// achievements rollup, in that order. Mobile renders as a right-edge
// drawer per doctrine D2; desktop/tablet render as an anchored popover.
import type { useDevImpersonationStorage } from "@/hooks/useDevImpersonationStorage";
import type { UseWorkplaceMessagesStateResult } from "@/components/messages/useWorkplaceMessagesState";
import { WorkplaceActivityNotificationsSection } from "@/components/messages/WorkplaceActivityNotificationsSection";
import { ActivityBannerImpersonationStrip } from "./ActivityBannerImpersonationStrip";
import { ActivityBannerAchievementsSection } from "./ActivityBannerAchievementsSection";
import { ActivityBannerAdAlertsSection } from "./ActivityBannerAdAlertsSection";
import { ActivityBannerGoogleCalendarSection } from "./ActivityBannerGoogleCalendarSection";

export const DESKTOP_PANEL_WIDTH = 380;

export type ActivityBannerPanelProps = {
  isMobile: boolean;
  panelRef: React.RefObject<HTMLDivElement | null>;
  position: { left: number; top: number };
  impersonation: ReturnType<typeof useDevImpersonationStorage>;
  wpState: UseWorkplaceMessagesStateResult;
  allowTestNotification: boolean;
  allowSenderLock: boolean;
  allowRequiresAck: boolean;
  open: boolean;
  onClose: () => void;
  onOpenAdsIngestion?: () => void;
};

export function ActivityBannerPanel({
  isMobile,
  panelRef,
  position,
  impersonation,
  wpState,
  allowTestNotification,
  allowSenderLock,
  allowRequiresAck,
  open,
  onClose,
  onOpenAdsIngestion,
}: ActivityBannerPanelProps) {
  const wrapperStyle: React.CSSProperties = isMobile
    ? { position: "fixed", inset: 0, zIndex: 60, pointerEvents: "none" }
    : {
        position: "fixed",
        left: position.left,
        top: position.top,
        width: DESKTOP_PANEL_WIDTH,
        maxHeight: "calc(100vh - 96px)",
        overflowY: "auto",
        zIndex: 60,
      };

  // `relative` on the panel is load-bearing on mobile (dev-sync 39282415):
  // the wrapper holds the backdrop button as `position: absolute inset-0`
  // first, then the panel as a sibling. With the panel as `position:
  // static`, browser paint order is "non-positioned descendants → then
  // positioned descendants" — so the absolute backdrop painted ON TOP of
  // the static panel, making the panel look unfocused/blurry through the
  // backdrop's `bg-black/40 backdrop-blur-[1px]`, and any tap landed on
  // the backdrop's onClick → onClose, dismissing instead of interacting.
  // Adding `relative` makes the panel itself positioned, so paint order
  // among the two positioned siblings reverts to DOM order (backdrop
  // first, panel second, panel on top). Same fix-class as the
  // `BugReportMobileEntry` portal slice (dev-sync 63d7f41e).
  const containerClass = isMobile
    ? "relative h-full w-[min(420px,90vw)] ml-auto flex flex-col border-l border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100"
    : "rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100";

  const adAlertsHandler = onOpenAdsIngestion
    ? () => {
        onOpenAdsIngestion();
        onClose();
      }
    : undefined;

  return (
    <div style={wrapperStyle}>
      {isMobile ? (
        <button
          type="button"
          aria-label="Close activity"
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
          style={{ pointerEvents: "auto" }}
        />
      ) : null}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Activity"
        className={containerClass}
        style={isMobile ? { pointerEvents: "auto" } : undefined}
      >
        <PanelHeader onClose={onClose} />
        {impersonation.enabled ? (
          <ActivityBannerImpersonationStrip impersonation={impersonation} />
        ) : null}
        <WorkplaceActivityNotificationsSection
          wpState={wpState}
          allowTestNotification={allowTestNotification}
          allowSenderLock={allowSenderLock}
          allowRequiresAck={allowRequiresAck}
          panelOpen={open}
          onClosePanel={onClose}
        />
        <ActivityBannerGoogleCalendarSection open={open} />
        <ActivityBannerAdAlertsSection open={open} onOpenAdsIngestion={adAlertsHandler} />
        <ActivityBannerAchievementsSection open={open} />
      </div>
    </div>
  );
}

function PanelHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Activity
        </div>
        <div className="mt-0.5 truncate text-sm font-semibold">Workplace activity</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close activity"
        className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        ✕
      </button>
    </div>
  );
}
